import {useWorkOrder} from "../../WorkOrdersStack";
import React, {useEffect, useState} from "react";
import {FlatList, ListRenderItemInfo, View} from "react-native";
import {Button, Card, Text} from "react-native-paper";
import Skeleton from "../../../../../../components/skeletons/Skeleton";
import {WorkOrderLog} from "../../../../../../store/data/entities/work-order-log";
import {useLazyGetWorkOrderLogsQuery} from "../../../../../../store/services/api/work-order-api";
import useUser from "../../../../../../hooks/useUser";
import {EditOutlined, FileOutlined} from "@ant-design/icons";
import {Badge, Button as AntButton, Flex} from "antd";
import UploadWorkOrderLogDocumentsModal from "../../../../../../components/modals/UploadWorkOrderLogDocumentsModal";
import AddWorkLogModal from "../../../../../../components/modals/New/AddWorkLogModal";
import {WorkOrderStatus} from "../../../../../../store/data/entities/work-order";
import { useFocusEffect } from '@react-navigation/native';

export default function TechnicianWorkLog() {
  const [getLogs, {data, isFetching}] = useLazyGetWorkOrderLogsQuery();
  const [workLogModalVisible, setWorkLogModalVisible] = useState(false);
  const [documentsModalVisible, setDocumentsModalVisible] = useState(false);
  const [selectedWorkOrderLog, setSelectedWorkOrderLog] = useState<WorkOrderLog | undefined>(undefined);
  const {id, workOrder} = useWorkOrder();
  const {user} = useUser();
  const isInProgress = workOrder?.status === WorkOrderStatus.InProgress;

  useEffect(() => {
    if (id) {
      getLogs(id);
    }
  }, [id]);

  useFocusEffect(
  React.useCallback(() => {
    if (id) {
      getLogs(id); 
    }
  }, [id])
);

  const openWorkLogEditModal = (item: WorkOrderLog) => {
    setSelectedWorkOrderLog(item);
    setWorkLogModalVisible(true);
  }

  const closeWorkLogEditModal = () => {
    setSelectedWorkOrderLog(undefined);
    setWorkLogModalVisible(false);
  }

  const openDocumentsModal = (item: WorkOrderLog) => {
    setSelectedWorkOrderLog(item);
    setDocumentsModalVisible(true);
  }

  const closeDocumentsModal = () => {
    setSelectedWorkOrderLog(undefined);
    setDocumentsModalVisible(false);
  }

  const renderWorkLog = ({item}: ListRenderItemInfo<WorkOrderLog>) => {
    const documentCount = item.fileURIs.length;
    return (
      <Card style={{backgroundColor: 'white'}}>
        <Card.Title title={item.user} />
        <Card.Content>
          <View style={{flexDirection: 'row', justifyContent: 'space-between'}}>
            <Text variant='bodySmall' style={{color: '#000000ff'}}>{item.comments}</Text>
            <Flex gap={4}>
              <AntButton
                disabled={!isInProgress || user?.id !== item.createdById}
                onClick={() => openWorkLogEditModal(item)}
                icon={<EditOutlined />}
              />

              <Badge count={documentCount} color='rgb(44, 137, 198)'>
                <AntButton
                  onClick={() => openDocumentsModal(item)}
                  icon={<FileOutlined />}
                />
              </Badge>
            </Flex>
          </View>
        </Card.Content>
      </Card>
    )
  }

  return (
    <Skeleton
      loading={isFetching}
      placeholder={<Skeleton.Card />}
    >
      <FlatList
        style={{paddingHorizontal: 4, paddingVertical: 6}}
        ItemSeparatorComponent={() => <View style={{height: 6}} />}
        data={data ?? []}
        renderItem={renderWorkLog}
        ListFooterComponent={(
          <Button
            style={{marginTop: 6}}
            icon='plus'
            mode="elevated"
            onPress={() => setWorkLogModalVisible(true)}
            disabled={!isInProgress}
            buttonColor='#fff'
            textColor='#0f0f0f'
          >
            Add Work Log
          </Button>
        )}
      />

      <AddWorkLogModal
        workOrderId={id}
        defaultUserId={user.id}
        previousWorkOrderLog={selectedWorkOrderLog}
        userRoles={['Technician', 'BuildingLead', 'ExternalTechnician']}
        onClose={closeWorkLogEditModal}
        onSuccess={closeWorkLogEditModal}
        open={workLogModalVisible}
      />

      <UploadWorkOrderLogDocumentsModal
        workOrderId={id}
        workOrderLogEntryId={selectedWorkOrderLog?.originalLogEntryId ?? selectedWorkOrderLog?.id}
        existingFileURIs={selectedWorkOrderLog?.fileURIs ?? []}
        onClose={closeDocumentsModal}
        visible={documentsModalVisible}
        viewOnly={user?.id !== selectedWorkOrderLog?.createdById}
      />
    </Skeleton>
  );
}
